import React, { createContext, useState, useContext, useEffect } from 'react';
import { chatService } from '../services/api';

const SettingsContext = createContext();

export const SettingsProvider = ({ children }) => {
  const [systemPrompt, setSystemPrompt] = useState('');
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [isEditingVectorSettings, setIsEditingVectorSettings] = useState(false);
  const [vectorClassifierPrompt, setVectorClassifierPrompt] = useState('');
  const [vectorClassifierThreshold, setVectorClassifierThreshold] = useState('');
  const [isProductClassifierOpen, setIsProductClassifierOpen] = useState(false);
  const [settings, setSettings] = useState({});
  const [isEditingVectorStorePrompt, setIsEditingVectorStorePrompt] = useState(false);
  const [vectorStorePrompt, setVectorStorePrompt] = useState('');
  const [vectorstoreId, setVectorstoreId] = useState('');
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [settingsError, setSettingsError] = useState(null);

  // Load vector classifier settings when modal opens
  useEffect(() => {
    const loadVectorSettings = async () => {
      if (isEditingVectorSettings) {
        setIsLoadingSettings(true);
        setSettingsError(null);
        try {
          const [prompt, threshold] = await Promise.all([
            chatService.getSetting('vector-classifier-prompt'),
            chatService.getSetting('vector-classifier-threshold')
          ]);
          setVectorClassifierPrompt(prompt || '');
          setVectorClassifierThreshold(threshold || '0.7');
        } catch (error) {
          console.error('Failed to load vector classifier settings:', error);
          setSettingsError('Failed to load settings. Please try again.');
        } finally {
          setIsLoadingSettings(false);
        }
      }
    };
    loadVectorSettings();
  }, [isEditingVectorSettings]);

  // Load system prompt when modal opens
  useEffect(() => {
    const loadSystemPrompt = async () => {
      if (isEditingPrompt) {
        setIsLoadingSettings(true);
        setSettingsError(null);
        try {
          const prompt = await chatService.getSetting('system-prompt');
          setSystemPrompt(prompt || '');
        } catch (error) {
          console.error('Failed to load system prompt:', error);
          setSettingsError('Failed to load system prompt. Please try again.');
        } finally {
          setIsLoadingSettings(false);
        }
      }
    };
    loadSystemPrompt();
  }, [isEditingPrompt]);

  // Load product classifier settings when modal opens
  useEffect(() => {
    const loadProductClassifierSettings = async () => {
      if (isProductClassifierOpen) {
        setIsLoadingSettings(true);
        setSettingsError(null);
        try {
          const [prompt, threshold] = await Promise.all([
            chatService.getSetting('product-classifier-prompt'),
            chatService.getSetting('product-classifier-threshold')
          ]);
          setSettings({
            'product-classifier-prompt': prompt || '',
            'product-classifier-threshold': threshold || ''
          });
        } catch (error) {
          console.error('Failed to load product classifier settings:', error);
          setSettingsError('Failed to load settings. Please try again.');
        } finally {
          setIsLoadingSettings(false);
        }
      }
    };
    loadProductClassifierSettings();
  }, [isProductClassifierOpen]);

  // Load vector store settings when modal opens
  useEffect(() => {
    const loadVectorStoreSettings = async () => {
      if (isEditingVectorStorePrompt) {
        setIsLoadingSettings(true);
        setSettingsError(null);
        try {
          const [prompt, vectorstoreId] = await Promise.all([
            chatService.getSetting('vector-store-prompt'),
            chatService.getSetting('vectorstore-id')
          ]);
          setVectorStorePrompt(prompt || '');
          setVectorstoreId(vectorstoreId || '');
        } catch (error) {
          console.error('Failed to load vector store settings:', error);
          setSettingsError('Failed to load settings. Please try again.');
        } finally {
          setIsLoadingSettings(false);
        }
      }
    };
    loadVectorStoreSettings();
  }, [isEditingVectorStorePrompt]);

  const handleUpdateSystemPrompt = async () => {
    setIsLoadingSettings(true);
    setSettingsError(null);
    try {
      await chatService.updateSetting('system-prompt', systemPrompt);
      setIsEditingPrompt(false);
    } catch (error) {
      console.error('Failed to update system prompt:', error);
      setSettingsError('Failed to update system prompt. Please try again.');
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const handleSaveVectorSettings = async () => {
    setIsLoadingSettings(true);
    setSettingsError(null);
    try {
      await Promise.all([
        chatService.updateSetting('vector-classifier-prompt', vectorClassifierPrompt),
        chatService.updateSetting('vector-classifier-threshold', vectorClassifierThreshold)
      ]);
      setIsEditingVectorSettings(false);
    } catch (error) {
      console.error('Failed to save vector settings:', error);
      setSettingsError('Failed to save settings. Please try again.');
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const handleSaveProductClassifierSettings = async () => {
    setIsLoadingSettings(true);
    setSettingsError(null);
    try {
      await Promise.all([
        chatService.updateSetting('product-classifier-prompt', settings['product-classifier-prompt']),
        chatService.updateSetting('product-classifier-threshold', settings['product-classifier-threshold'])
      ]);
      setIsProductClassifierOpen(false);
    } catch (error) {
      console.error('Failed to save product classifier settings:', error);
      setSettingsError('Failed to save settings. Please try again.');
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const handleSaveVectorStoreSettings = async () => {
    setIsLoadingSettings(true);
    setSettingsError(null);
    try {
      await Promise.all([
        chatService.updateSetting('vector-store-prompt', vectorStorePrompt),
        chatService.updateSetting('vectorstore-id', vectorstoreId)
      ]);
      setIsEditingVectorStorePrompt(false);
    } catch (error) {
      console.error('Failed to save vector store settings:', error);
      setSettingsError('Failed to save settings. Please try again.');
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const handleSettingChange = (setting, value) => {
    setSettings({ ...settings, [setting]: value });
  };

  const value = {
    systemPrompt,
    setSystemPrompt,
    isEditingPrompt,
    setIsEditingPrompt,
    showSettingsModal,
    setShowSettingsModal,
    isEditingVectorSettings,
    setIsEditingVectorSettings,
    vectorClassifierPrompt,
    setVectorClassifierPrompt,
    vectorClassifierThreshold,
    setVectorClassifierThreshold,
    isProductClassifierOpen,
    setIsProductClassifierOpen,
    settings,
    setSettings,
    handleSettingChange,
    isEditingVectorStorePrompt,
    setIsEditingVectorStorePrompt,
    vectorStorePrompt,
    setVectorStorePrompt,
    vectorstoreId,
    setVectorstoreId,
    isLoadingSettings,
    settingsError,
    handleUpdateSystemPrompt,
    handleSaveVectorSettings,
    handleSaveProductClassifierSettings,
    handleSaveVectorStoreSettings
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
